/**
 * placementPreviewGeometry -- pure math for the on-garment placement preview
 * (Store Decorations redesign, pebble 3b, ADR 0065).
 *
 * Card: https://trello.com/c/oSOHJfg0
 *
 * This module has THREE responsibilities, all pure and unit-tested:
 *
 *   1. computePlacementVerdict -- a faithful FRONTEND MIRROR of the canonical
 *      backend ``api/src/services/placement_compatibility.compute_verdict``.
 *      The backend owns the authority; this mirror exists only because the
 *      Store Decorations page has no per-placement verdict in the rollup and
 *      cannot round-trip a batch call per open accordion. It reproduces the
 *      SAME arithmetic (10.0% strict-exclusive re-approval threshold,
 *      size_delta_pct = max axis delta, size-class zone_scale applied to the
 *      zone envelope BEFORE the fit comparison) so the pill reads identically
 *      to the product editor and bulk-apply surfaces (ADR 0065 / 0046).
 *      **Keep in lockstep with the Python.** ADR 0065: the verdict is exact
 *      because it is arithmetic on inches, not on the drawing -- so even a
 *      rough/generic preview carries an exact verdict.
 *
 *   2. zoneInchesFor -- resolves a placement zone's printable area in inches.
 *      Prefers explicit ``default_max_width_in`` / ``default_max_height_in`` on
 *      the zone record (what P1 seeded and the SA editor tunes; the same
 *      columns compute_verdict reads), falling back to a slug-keyed table of
 *      the backend-seeded defaults so a preview still renders when the rollup
 *      has not surfaced dims for that placement yet.
 *
 *   3. hotspotFor / overlayUnitsFor / offeredZoneSlugs -- the silhouette
 *      hotspot geometry. The repo's silhouettes (src/assets/silhouettes/*.svg)
 *      are IMAGE files with NO authored per-zone hotspot boxes (unlike ADR
 *      0065's assumed ``silhouettes-data.js hotspots[]``); the per-SKU boxes
 *      that DO exist come from a backend endpoint this frontend-only unit
 *      cannot call. So we author a static hotspot table in the repo's own SVG
 *      viewBox units (apparel 120x140, headwear 140x100) for the corpus
 *      silhouettes, keyed by the backend's underscore zone slugs. Where a
 *      (silhouette, zone) pair has no hotspot -- or the product is on the
 *      ``generic`` silhouette -- ``hotspotFor`` returns null and the component
 *      falls back to a rough/generic preview (Jake's locked best-effort rule),
 *      while the verdict above stays exact.
 */

// ---------------------------------------------------------------------------
// 1. Verdict mirror (backend compute_verdict).
// ---------------------------------------------------------------------------

// Strict-exclusive resize threshold per P4-Q11 (mirrors the backend
// RESIZE_THRESHOLD_PCT). Exactly 10.0% is REMAP; 10.0000001% is DROP.
export const RESIZE_THRESHOLD_PCT = 10.0;

/**
 * Compute the fit / remap / drop verdict for a decoration against a zone.
 * Frontend mirror of the backend ``compute_verdict``. Returns the same shape
 * ``PlacementCompatibilityIndicator.vue`` consumes:
 *
 *     { verdict: "fit"|"remap"|"drop"|"blocked", reason, size_delta_pct }
 *
 * "blocked" is returned for invalid / missing dimensions (a superset of the
 * backend's DROP-on-bad-input, surfaced as the indicator's neutral pill so the
 * page never shows a fake "Fits" for un-computable input).
 *
 * @param {object} args
 * @param {number} args.widthIn         source imprint width (inches)
 * @param {number} args.heightIn        source imprint height (inches)
 * @param {number|null} args.zoneWidthIn  zone max width (inches)
 * @param {number|null} args.zoneHeightIn zone max height (inches)
 * @param {number|null} [args.sizeScaleFactor] youth ~0.78, adult 1.0; NULL -> 1.0
 * @returns {{verdict: string, reason: string, size_delta_pct: number|null}}
 */
export function computePlacementVerdict({
  widthIn,
  heightIn,
  zoneWidthIn,
  zoneHeightIn,
  sizeScaleFactor = 1.0,
} = {}) {
  // A NULL product scale means "adult" (the column default is 1.0 and legacy
  // products predate the column) -- normalize so it is a no-op, matching the
  // backend. A non-positive scale is genuinely invalid.
  let scale = sizeScaleFactor;
  if (scale === null || scale === undefined) scale = 1.0;

  if (
    zoneWidthIn === null ||
    zoneWidthIn === undefined ||
    zoneHeightIn === null ||
    zoneHeightIn === undefined ||
    widthIn === null ||
    widthIn === undefined ||
    heightIn === null ||
    heightIn === undefined ||
    !(scale > 0) ||
    !(zoneWidthIn > 0) ||
    !(zoneHeightIn > 0) ||
    !(widthIn > 0) ||
    !(heightIn > 0)
  ) {
    return {
      verdict: "blocked",
      reason: "Invalid or missing dimensions; cannot compute fit.",
      size_delta_pct: null,
    };
  }

  // Apply the size-class scale (ADR 0065): the effective printable area is the
  // zone envelope shrunk by the target garment's scale. All comparisons run
  // against these effective maxes so preview and verdict agree on youth/adult.
  const effW = zoneWidthIn * scale;
  const effH = zoneHeightIn * scale;

  const fmt = (n) => `${n.toFixed(1)}"`;

  if (widthIn <= effW && heightIn <= effH) {
    return {
      verdict: "fit",
      reason: `${fmt(widthIn)} x ${fmt(heightIn)} fits a ${fmt(effW)} x ${fmt(effH)} area.`,
      size_delta_pct: 0,
    };
  }

  // Minimum resize that fits the zone while preserving aspect ratio; the
  // binding (smaller) axis scale wins.
  const scaleW = effW / widthIn;
  const scaleH = effH / heightIn;
  const fitScale = Math.min(scaleW, scaleH);

  const newW = widthIn * fitScale;
  const newH = heightIn * fitScale;

  const deltaWPct = (Math.abs(newW - widthIn) / widthIn) * 100.0;
  const deltaHPct = (Math.abs(newH - heightIn) / heightIn) * 100.0;
  const sizeDeltaPct = Math.max(deltaWPct, deltaHPct);

  // Strict-exclusive boundary (P4-Q11): exactly 10.0% is REMAP; anything
  // greater is DROP. This MUST match the backend byte-for-byte: compute_verdict
  // in api/src/services/placement_compatibility.py uses a BARE
  // ``if size_delta_pct > RESIZE_THRESHOLD_PCT`` -- no epsilon. An epsilon here
  // would let the interval (10.0, 10.0 + eps] read as remap in the preview while
  // the authority read drop, so the pill would misreport fit vs the backend.
  // Keep it strict-exclusive ``>`` with no slack so the two never diverge.
  if (sizeDeltaPct > RESIZE_THRESHOLD_PCT) {
    return {
      verdict: "drop",
      reason:
        `Required resize is ${sizeDeltaPct.toFixed(1)}% which exceeds the ` +
        `${RESIZE_THRESHOLD_PCT.toFixed(0)}% re-approval threshold. ` +
        "Treated as a new decoration.",
      size_delta_pct: sizeDeltaPct,
    };
  }

  return {
    verdict: "remap",
    reason: `Artwork can fit ${fmt(effW)} x ${fmt(effH)} if resized by ${sizeDeltaPct.toFixed(1)}%.`,
    size_delta_pct: sizeDeltaPct,
  };
}

// ---------------------------------------------------------------------------
// 2. Zone printable inches.
// ---------------------------------------------------------------------------

// Slug-keyed fallback printable area (inches), mirroring the backend
// per-view MINT HEURISTIC in api/src/services/placement_zone_view_code.py
// ``default_dims_for_view_code`` (chest 4.0x4.0, sleeve 3.0x12.0, full/center
// back 12.0x14.0, upper_back 10.0x4.0, cap front 4.0x2.5, cap side 2.5x2.5)
// plus ADR 0065's table for the zones that heuristic does not enumerate
// (center chest, hood, bucket band). These are PROVISIONAL -- the authority is
// placement_zones.default_max_* (P1 seed, SA-editable), which zoneInchesFor
// prefers when present on the record. Used ONLY when the rollup has not
// surfaced dims for a placement's zone.
//
// TWO BACKEND SOURCES, DELIBERATELY (read before "fixing" a mismatch). The
// SEEDED catalog rows written by migration ``store_deco_zone_dims_001`` carry
// ADR 0065's refined figures -- left_chest 4.5x4.5, full_back 14.0x16.0,
// upper_back 12.0x4.0, left/right_sleeve 3.5x12.0. This table intentionally
// mirrors the MINT HEURISTIC instead, because that is what a zone with no
// seeded row resolves to, which is exactly the case this fallback covers. The
// numbers therefore differ from the seed by design and the difference is
// invisible in practice: zoneInchesFor prefers an explicit record whenever the
// rollup carries one. Changing one side to "match" the other silently
// re-scales every preview whose zone was minted rather than seeded. The
// mirror is pinned by the "sleeve dimensions -- single source" spec.
const ZONE_INCHES_BY_SLUG = Object.freeze({
  left_chest: [4.0, 4.0],
  right_chest: [4.0, 4.0],
  center_chest: [12.0, 12.0],
  full_back: [12.0, 14.0],
  upper_back: [10.0, 4.0],
  left_sleeve: [3.0, 12.0],
  right_sleeve: [3.0, 12.0],
  hood_front: [8.0, 3.0],
  hood_back: [8.0, 4.0],
  cap_front_panel: [4.0, 2.5],
  cap_side_panel: [2.5, 2.5],
  side_panel: [2.5, 2.5],
  side_band: [4.0, 1.5],
  back_closure: [2.0, 1.5],
  // --- Silhouette-corpus extension (bottoms / bags / pouch pocket). ---
  // PROVISIONAL, and more provisional than the rows above: these zones have NO
  // backend authority at all today -- no seeded placement_zones row, and
  // ``default_dims_for_view_code`` has no branch for a leg, a hip or a bag
  // panel, so it hands every one of them the generic 4.0x4.0. That generic is
  // plainly wrong for a leg (a thigh imprint is tall) and for a bag front
  // panel (much larger than a chest), which is why they are enumerated here.
  // Figures are chosen from the physical garment, in the same spirit as ADR
  // 0065's table, and are trivially re-tunable -- when real decorator dims
  // arrive from Dustin they land in placement_zones and win automatically.
  //
  // Leg: a 4x10 panel down the outside of the thigh.
  left_leg: [4.0, 10.0],
  right_leg: [4.0, 10.0],
  // Hip: a small square patch, chest-sized, front or back.
  front_hip_left: [4.0, 4.0],
  front_hip_right: [4.0, 4.0],
  back_hip_left: [4.0, 4.0],
  back_hip_right: [4.0, 4.0],
  // Bag panels, smallest to largest. Front Top Center is the strap-side band
  // above the main panel; Front Center Center is the main panel; Front Full is
  // the whole printable face.
  front_top_center: [8.0, 4.0],
  front_center_center: [10.0, 8.0],
  front_full: [12.0, 10.0],
  // Hoodie pouch: wider than tall, and split by the pocket opening.
  pouch_pocket: [8.0, 5.0],
});

/**
 * The printable area (inches) for a placement zone, as ``[w, h]`` or null.
 * Prefers explicit dims on the zone record (authority: the columns
 * compute_verdict reads), else the seeded slug table.
 *
 * @param {{slug?: string, default_max_width_in?: number, default_max_height_in?: number}|null} zone
 * @returns {[number, number]|null}
 */
export function zoneInchesFor(zone) {
  if (!zone) return null;
  const w = zone.default_max_width_in;
  const h = zone.default_max_height_in;
  if (typeof w === "number" && w > 0 && typeof h === "number" && h > 0) {
    return [w, h];
  }
  const slug = typeof zone.slug === "string" ? zone.slug : null;
  if (slug && Object.prototype.hasOwnProperty.call(ZONE_INCHES_BY_SLUG, slug)) {
    return [...ZONE_INCHES_BY_SLUG[slug]];
  }
  return null;
}

// ---------------------------------------------------------------------------
// 3. Silhouette hotspot geometry.
// ---------------------------------------------------------------------------

// Per-(silhouette, zone-slug) hotspot boxes in the repo's SVG viewBox units.
// Apparel silhouettes use a 120x140 viewBox; headwear (baseballCap/bucketHat)
// use 140x100. Each entry is { face, x, y, w, h } where (x, y) is the box
// CENTRE (matching the ADR 0065 / sd-data.jsx GarmentPreview convention) and
// (w, h) the box size, both in viewBox units. These recreate ADR 0065's
// per-view hotspots[] against the repo's own silhouettes (the repo has none
// authored). PROVISIONAL geometry -- proportions may be revisited once real
// zone dims land (ADR 0065 "Consequences").
//
// SIDE CONVENTION (Jake, 2026-10-22): left_chest is the WEARER'S left, which on
// a front-facing garment is the VIEWER'S RIGHT (x > centre); right_chest is the
// mirror. This table is the single place the side is decided -- the Stage
// silhouette marks, the supplier-photo mapping (photoBoxFor) and the shopper
// GarmentPreview all read it. Zone KEYS are unchanged (they are the stored and
// ordered names); only where they are drawn moved. EVERY left_*/right_* pair
// follows it on a FRONT face (chest, sleeve, leg, front hip, Jake 2026-10-22);
// on a BACK face the viewer sees the wearer's back, so back_hip_left stays on
// the viewer's left. A silhouette absent here, or a zone absent for a
// silhouette, yields null -> the rough/generic fallback.
//
// INVARIANT (load-bearing for true relative scale, ADR 0065): every box whose
// zone resolves to printable inches is authored to that zone's ASPECT RATIO
// (box w:h == zone width_in:height_in). Because overlayUnitsFor derives a
// single width-reference units-per-inch, an aspect-matched box guarantees a
// zone-filling mark also fills the box's HEIGHT, so the mark scales truly on
// both axes and aligns with its own dashed zone box. Non-square zones (sleeves
// 3x12, upper_back 10x4, hoods 8x3/8x4, cap front 4x2.5, bucket band 4x1.5,
// legs 4x10, bag top panel 8x4, pouch 8x5) get narrow-tall / short-wide boxes
// accordingly. Square chest/center/hip zones stay square. The invariant is
// pinned by the "boxes are authored to zone aspect" test, which walks
// silhouetteClasses() and resolves inches through zoneInchesFor -- so it binds
// on every member of this table, including ones added after it was written.
// If you edit a box, keep its w/h == zone width_in/height_in.
const HOTSPOTS = Object.freeze({
  // Apparel (front primarily; back for back/sleeve zones). viewBox 120x140.
  tee: {
    left_chest: { face: "front", x: 76, y: 52, w: 22, h: 22 },
    right_chest: { face: "front", x: 44, y: 52, w: 22, h: 22 },
    center_chest: { face: "front", x: 60, y: 66, w: 52, h: 52 },
    full_back: { face: "back", x: 60, y: 74, w: 54, h: 63 },
    upper_back: { face: "back", x: 60, y: 46, w: 50, h: 20 },
    left_sleeve: { face: "front", x: 94, y: 56, w: 9, h: 36 },
    right_sleeve: { face: "front", x: 26, y: 56, w: 9, h: 36 },
  },
  polo: {
    left_chest: { face: "front", x: 76, y: 54, w: 22, h: 22 },
    right_chest: { face: "front", x: 44, y: 54, w: 22, h: 22 },
    center_chest: { face: "front", x: 60, y: 68, w: 50, h: 50 },
    full_back: { face: "back", x: 60, y: 74, w: 54, h: 63 },
    upper_back: { face: "back", x: 60, y: 46, w: 50, h: 20 },
    left_sleeve: { face: "front", x: 94, y: 56, w: 9, h: 36 },
    right_sleeve: { face: "front", x: 26, y: 56, w: 9, h: 36 },
  },
  crewneck: {
    left_chest: { face: "front", x: 76, y: 54, w: 22, h: 22 },
    right_chest: { face: "front", x: 44, y: 54, w: 22, h: 22 },
    center_chest: { face: "front", x: 60, y: 68, w: 52, h: 52 },
    full_back: { face: "back", x: 60, y: 74, w: 54, h: 63 },
    upper_back: { face: "back", x: 60, y: 46, w: 50, h: 20 },
    left_sleeve: { face: "front", x: 94, y: 58, w: 9, h: 36 },
    right_sleeve: { face: "front", x: 26, y: 58, w: 9, h: 36 },
  },
  hoodie: {
    left_chest: { face: "front", x: 76, y: 58, w: 22, h: 22 },
    right_chest: { face: "front", x: 44, y: 58, w: 22, h: 22 },
    center_chest: { face: "front", x: 60, y: 72, w: 50, h: 50 },
    full_back: { face: "back", x: 60, y: 76, w: 54, h: 63 },
    upper_back: { face: "back", x: 60, y: 48, w: 50, h: 20 },
    left_sleeve: { face: "front", x: 96, y: 60, w: 9, h: 36 },
    right_sleeve: { face: "front", x: 24, y: 60, w: 9, h: 36 },
    hood_front: { face: "front", x: 60, y: 26, w: 32, h: 12 },
    hood_back: { face: "back", x: 60, y: 26, w: 30, h: 15 },
    // 48 products in the Artifi location sheet expose Pouch Pocket and every
    // one is a hooded PULLOVER (ST225/226/230/271/280/296, NEA137, F244, ...).
    // Sits over the drawn pouch on hoodie-front.svg (the y 86-112 patch).
    pouch_pocket: { face: "front", x: 60, y: 99, w: 40, h: 25 },
  },
  fullZip: {
    left_chest: { face: "front", x: 76, y: 54, w: 20, h: 20 },
    right_chest: { face: "front", x: 44, y: 54, w: 20, h: 20 },
    // Audit gap: tee/polo/crewneck/hoodie all offered center_chest and fullZip
    // did not, though a full-zip plainly takes a centred front imprint. It is
    // split down the middle by the zip -- which is a thing the preview should
    // SHOW, not a reason to withhold the zone. Deliberately NO pouch_pocket
    // here: a full-zip front has no pouch to print on.
    center_chest: { face: "front", x: 60, y: 70, w: 48, h: 48 },
    full_back: { face: "back", x: 60, y: 74, w: 54, h: 63 },
    upper_back: { face: "back", x: 60, y: 46, w: 50, h: 20 },
    left_sleeve: { face: "front", x: 94, y: 56, w: 9, h: 36 },
    right_sleeve: { face: "front", x: 26, y: 56, w: 9, h: 36 },
  },
  // Headwear. viewBox 140x100. Front crown + side panel/band + back closure.
  baseballCap: {
    cap_front_panel: { face: "front", x: 70, y: 42, w: 40, h: 25 },
    front_crown: { face: "front", x: 70, y: 42, w: 40, h: 25 },
    cap_side_panel: { face: "side", x: 70, y: 42, w: 26, h: 26 },
    side_panel: { face: "side", x: 70, y: 42, w: 26, h: 26 },
    back_closure: { face: "side", x: 30, y: 46, w: 18, h: 13.5 },
  },
  bucketHat: {
    side_band: { face: "side", x: 70, y: 62, w: 40, h: 15 },
    cap_side_panel: { face: "side", x: 70, y: 62, w: 24, h: 24 },
    side_panel: { face: "side", x: 70, y: 62, w: 24, h: 24 },
  },
  // Bottoms (pants / shorts / joggers). viewBox 120x140. Legs on the front
  // face, hips on the face their Artifi view code names. Product counts from
  // the SanMar Artifi location sheet ("Location With Product", 2,862 products):
  // Front Hip Right 107, Front Hip Left 105, Left Leg 104, Right Leg 104,
  // Back Hip Left 97, Back Hip Right 91 -- 109 distinct products in total,
  // ALL of which routed to `generic` (no hotspot at all) before this entry.
  bottoms: {
    left_leg: { face: "front", x: 77, y: 96, w: 14, h: 35 },
    right_leg: { face: "front", x: 43, y: 96, w: 14, h: 35 },
    front_hip_left: { face: "front", x: 76, y: 54, w: 16, h: 16 },
    front_hip_right: { face: "front", x: 44, y: 54, w: 16, h: 16 },
    back_hip_left: { face: "back", x: 44, y: 54, w: 16, h: 16 },
    back_hip_right: { face: "back", x: 76, y: 54, w: 16, h: 16 },
  },
  // Bags / totes / duffels / packs. viewBox 120x140 (the same apparel box, so
  // the whole corpus renders at one scale). 240 products expose Front Top
  // Center -- the largest single un-silhouetted group in the sheet -- and all
  // 240 expose exactly the SAME three front views, with 211 also exposing
  // Rear. So the three front zones below are not a guess at what a bag might
  // offer; they are the complete front set for every bag in the feed.
  bag: {
    front_top_center: { face: "front", x: 60, y: 44, w: 40, h: 20 },
    front_center_center: { face: "front", x: 60, y: 76, w: 50, h: 40 },
    front_full: { face: "front", x: 60, y: 78, w: 60, h: 50 },
  },
  // generic: intentionally EMPTY -- a product on the generic silhouette gets a
  // rough preview (no hotspot), verdict still exact.
  generic: {},
});

// The viewBox for each silhouette class, matching the repo's SVG assets.
const VIEWBOX = Object.freeze({
  tee: [120, 140],
  polo: [120, 140],
  crewneck: [120, 140],
  hoodie: [120, 140],
  fullZip: [120, 140],
  bottoms: [120, 140],
  bag: [120, 140],
  baseballCap: [140, 100],
  bucketHat: [140, 100],
  generic: [120, 140],
});

/**
 * The viewBox [w, h] for a silhouette class (falls back to apparel 120x140).
 * @param {string} silhouette
 * @returns {[number, number]}
 */
export function viewBoxFor(silhouette) {
  return VIEWBOX[silhouette] ? [...VIEWBOX[silhouette]] : [120, 140];
}

// Where the GARMENT sits inside each silhouette's viewBox (min x, min y, max x,
// max y), read off the outline paths in src/assets/silhouettes/*-front.svg. The
// hotspot boxes above are in viewBox units, and the drawing does not fill its
// viewBox, so a box cannot be placed on anything but that drawing without this.
// Only the classes whose outline was measured are listed: hats and caps are
// not, so they get no photo calibration (photoBoxFor returns null).
const GARMENT_BBOX = Object.freeze({
  tee: [18, 28, 102, 128],
  polo: [18, 28, 102, 128],
  crewneck: [18, 28, 102, 128],
  hoodie: [18, 28, 102, 128],
  fullZip: [18, 28, 102, 128],
  generic: [18, 28, 102, 128],
  bottoms: [30, 22, 90, 132],
  bag: [24, 34, 96, 118],
});

// Where the garment sits inside a supplier's flat-lay PHOTO, as fractions of
// the frame: the photo fills it almost edge to edge (measured on SanMar PC380:
// x 0.027..0.981, y 0.030..0.996), unlike the drawing, which sits in a pad.
const PHOTO_GARMENT_FRAME = Object.freeze({ x0: 0.03, y0: 0.035, x1: 0.98, y1: 0.995 });

/**
 * A zone box (viewBox units, top-left x/y plus w/h) mapped onto a supplier
 * PHOTO of the garment, as percentages of the photo frame; null for a
 * silhouette with no measured garment outline (the caller keeps the drawing
 * calibration). The garment outline is stretched onto the photo's garment
 * frame, so a left-chest box lands on the upper chest instead of mid-torso.
 *
 * @param {string} silhouette
 * @param {number} ux  box left, viewBox units
 * @param {number} uy  box top, viewBox units
 * @param {number} uw  box width, viewBox units
 * @param {number} uh  box height, viewBox units
 * @returns {{leftPct:number, topPct:number, wPct:number, hPct:number}|null}
 */
export function photoBoxFor(silhouette, ux, uy, uw, uh) {
  const bbox = GARMENT_BBOX[silhouette];
  if (!bbox) return null;
  const [bx0, by0, bx1, by1] = bbox;
  const { x0, y0, x1, y1 } = PHOTO_GARMENT_FRAME;
  const sx = (x1 - x0) / (bx1 - bx0);
  const sy = (y1 - y0) / (by1 - by0);
  return {
    leftPct: (x0 + (ux - bx0) * sx) * 100,
    topPct: (y0 + (uy - by0) * sy) * 100,
    wPct: uw * sx * 100,
    hPct: uh * sy * 100,
  };
}

/**
 * The hotspot box for a (silhouette, zone-slug) pair, or null when the
 * silhouette does not offer that zone (blocked-zone / generic-fallback path).
 *
 * @param {string} silhouette   garment class (tee|polo|...|generic)
 * @param {string} zoneSlug     underscore zone slug (left_chest, ...)
 * @returns {{face: string, x: number, y: number, w: number, h: number}|null}
 */
export function hotspotFor(silhouette, zoneSlug) {
  const table = HOTSPOTS[silhouette];
  if (!table) return null;
  const hot = table[zoneSlug];
  return hot ? { ...hot } : null;
}

/**
 * The zone slugs a silhouette offers (authored hotspots). Empty for generic.
 * @param {string} silhouette
 * @returns {string[]}
 */
export function offeredZoneSlugs(silhouette) {
  const table = HOTSPOTS[silhouette];
  return table ? Object.keys(table) : [];
}

/**
 * Every silhouette class this module has authored geometry for, including
 * ``generic``.
 *
 * Exists so the zone-aspect invariant test can WALK the real table instead of
 * re-listing the classes by hand. A hand-copied list stops covering the corpus
 * the moment someone adds a silhouette and forgets to extend it -- which is
 * precisely the failure the invariant is there to prevent.
 *
 * @returns {string[]}
 */
export function silhouetteClasses() {
  return Object.keys(HOTSPOTS);
}

/**
 * The true-relative-scale overlay geometry for a decoration on a hotspot, in
 * the silhouette's viewBox units. The zone envelope is shrunk by the size-class
 * scale first (ADR 0065) so the hotspot box represents the EFFECTIVE printable
 * area -- a mark keeps its physical size while youth garments shrink the area
 * around it, making the same mark occupy more of the box.
 *
 *     units_per_inch = hotspot.w / (zoneWidthIn * scale)
 *     overlay_w      = widthIn  * units_per_inch
 *     overlay_h      = heightIn * units_per_inch
 *
 * centred on the hotspot centre. Returns { cx, cy, w, h } in viewBox units.
 *
 * @param {object} args
 * @param {{x,y,w,h}} args.hot
 * @param {number} args.zoneWidthIn
 * @param {number} args.zoneHeightIn
 * @param {number} args.widthIn
 * @param {number} args.heightIn
 * @param {number|null} [args.sizeScaleFactor]
 * @returns {{cx: number, cy: number, w: number, h: number}}
 */
export function overlayUnitsFor({
  hot,
  zoneWidthIn,
  zoneHeightIn,
  widthIn,
  heightIn,
  sizeScaleFactor = 1.0,
}) {
  let scale = sizeScaleFactor;
  if (scale === null || scale === undefined || !(scale > 0)) scale = 1.0;
  // A SINGLE, consistent units-per-inch on BOTH axes (ADR 0065 "true relative
  // scale"): the width axis is the reference. Using one scale keeps the drawn
  // mark's own aspect ratio (no distortion) -- a 2:1 mark stays 2:1 on screen.
  //
  // This is correct on both axes ONLY because the hotspot box is authored to its
  // zone's aspect ratio (see HOTSPOTS below): for an aspect-matched box,
  //     hot.w / effZoneW == hot.h / effZoneH,
  // so the width-derived upi also renders a zone-filling mark at the box's full
  // height, and the mark aligns with its own hotspot zone box. Averaging the two
  // axis scales (the earlier bug) produced neither axis's true scale on a
  // non-square zone -- the mark rendered at the wrong size and floated off its
  // box. Do NOT reintroduce an average; fix any misalignment by correcting the
  // box aspect, not by blending scales.
  const effZoneW = zoneWidthIn * scale;
  const upi = hot.w / effZoneW;
  return {
    cx: hot.x,
    cy: hot.y,
    w: widthIn * upi,
    h: heightIn * upi,
  };
}

export default {
  RESIZE_THRESHOLD_PCT,
  computePlacementVerdict,
  zoneInchesFor,
  hotspotFor,
  offeredZoneSlugs,
  silhouetteClasses,
  overlayUnitsFor,
  viewBoxFor,
  photoBoxFor,
};
